import { AuthorizationChannel, NormalizedCardStamp } from './authorizationChannels';

export type MerchantNumberValue = string | 'GDS_ONLY' | 'NOT_ACCEPTED';

export interface MerchantNumberRule {
	carrierCode: string;
	carrierName: string;
	cardLabel: string;
	cardStamps: NormalizedCardStamp[];
	merchantNumber: MerchantNumberValue;
	installmentsLabel: string;
	validUntil: string;
	notes?: string;
	/**
	 * Por dónde se autoriza, en las reglas de la base (Mesa de cruces → Reglas de cruce, `toMerchantRule`). Las de la
	 * tabla fija no lo traen: su canal sale de `authorizationChannels`.
	 */
	channel?: AuthorizationChannel;
}

/**
 * La tabla fija: el respaldo del modal de autorización y de "Códigos de comercio y cuotas" cuando la Support API no da
 * las reglas de la base (`features/authorization/merchantRules`), que son la fuente.
 */
export const MERCHANT_NUMBER_RULES: MerchantNumberRule[] = [
	// Aerolíneas Argentinas (AR)
	{
		carrierCode: 'AR',
		carrierName: 'Aerolíneas Argentinas',
		cardLabel: 'American Express',
		cardStamps: ['amex'],
		merchantNumber: '9960019958',
		installmentsLabel: '1 a 6',
		validUntil: '31/12/2026',
	},
	{
		carrierCode: 'AR',
		carrierName: 'Aerolíneas Argentinas',
		cardLabel: 'Master',
		cardStamps: ['mastercard'],
		merchantNumber: '00973809',
		installmentsLabel: '1 a 6',
		validUntil: '31/12/2026',
	},
	{
		carrierCode: 'AR',
		carrierName: 'Aerolíneas Argentinas',
		cardLabel: 'Visa',
		cardStamps: ['visa'],
		merchantNumber: '2047347001',
		installmentsLabel: '10',
		validUntil: '31/07/2026',
	},
	{
		carrierCode: 'AR',
		carrierName: 'Aerolíneas Argentinas',
		cardLabel: 'Visa',
		cardStamps: ['visa'],
		merchantNumber: '2047347001',
		installmentsLabel: '1 a 6',
		validUntil: '31/12/2026',
	},
	{
		carrierCode: 'AR',
		carrierName: 'Aerolíneas Argentinas',
		cardLabel: 'Oca',
		cardStamps: ['oca'],
		merchantNumber: '81100',
		installmentsLabel: '1 a 6',
		validUntil: '31/12/2026',
	},

	// Air Europa (UX)
	{
		carrierCode: 'UX',
		carrierName: 'Air Europa',
		cardLabel: 'American Express',
		cardStamps: ['amex'],
		merchantNumber: '9960154235',
		installmentsLabel: '1',
		validUntil: '31/12/2026',
	},
	{
		carrierCode: 'UX',
		carrierName: 'Air Europa',
		cardLabel: 'Master',
		cardStamps: ['mastercard'],
		merchantNumber: '18352329',
		installmentsLabel: '1 a 12',
		validUntil: '31/12/2026',
	},
	{
		carrierCode: 'UX',
		carrierName: 'Air Europa',
		cardLabel: 'Oca',
		cardStamps: ['oca'],
		merchantNumber: '18352329',
		installmentsLabel: '1 a 12',
		validUntil: '31/12/2026',
	},
	{
		carrierCode: 'UX',
		carrierName: 'Air Europa',
		cardLabel: 'Visa',
		cardStamps: ['visa'],
		merchantNumber: '18352329',
		installmentsLabel: '1 a 12',
		validUntil: '31/12/2026',
	},

	// Air France (AF)
	{
		carrierCode: 'AF',
		carrierName: 'Air France',
		cardLabel: 'Master',
		cardStamps: ['mastercard'],
		merchantNumber: 'GDS_ONLY',
		installmentsLabel: '1',
		validUntil: '31/12/2026',
		notes: 'Solo por GDS. Un pago. Venta internacional.',
	},
	{
		carrierCode: 'AF',
		carrierName: 'Air France',
		cardLabel: 'American Express',
		cardStamps: ['amex'],
		merchantNumber: 'GDS_ONLY',
		installmentsLabel: '1',
		validUntil: '31/12/2026',
		notes: 'Solo por GDS. Un pago. Venta internacional.',
	},

	// American Airlines (AA)
	{
		carrierCode: 'AA',
		carrierName: 'American Airlines',
		cardLabel: 'American Express',
		cardStamps: ['amex'],
		merchantNumber: '9960047553',
		installmentsLabel: '1 a 6',
		validUntil: '31/07/2026',
	},
	{
		carrierCode: 'AA',
		carrierName: 'American Airlines',
		cardLabel: 'Oca',
		cardStamps: ['oca'],
		merchantNumber: '103413',
		installmentsLabel: '1 a 6',
		validUntil: '31/07/2026',
	},
	{
		carrierCode: 'AA',
		carrierName: 'American Airlines',
		cardLabel: 'Master',
		cardStamps: ['mastercard'],
		merchantNumber: '04339467',
		installmentsLabel: '1 a 6',
		validUntil: '31/07/2026',
	},
	{
		carrierCode: 'AA',
		carrierName: 'American Airlines',
		cardLabel: 'Visa',
		cardStamps: ['visa'],
		merchantNumber: '1005503002',
		installmentsLabel: '1 a 6',
		validUntil: '31/07/2026',
	},

	// Avianca (AV)
	{
		carrierCode: 'AV',
		carrierName: 'Avianca',
		cardLabel: 'OCA',
		cardStamps: ['oca'],
		merchantNumber: '27032332',
		installmentsLabel: '1 a 6',
		validUntil: '31/12/2026',
		notes: 'Cuotas solo tarjetas emitidas en bancos locales de Uruguay.',
	},
	{
		carrierCode: 'AV',
		carrierName: 'Avianca',
		cardLabel: 'Master',
		cardStamps: ['mastercard'],
		merchantNumber: '27032332',
		installmentsLabel: '1 a 6',
		validUntil: '31/12/2026',
		notes: 'Cuotas solo tarjetas emitidas en bancos locales de Uruguay.',
	},
	{
		carrierCode: 'AV',
		carrierName: 'Avianca',
		cardLabel: 'Visa',
		cardStamps: ['visa'],
		merchantNumber: '2110817002',
		installmentsLabel: '1 a 6',
		validUntil: '30/12/2026',
		notes: 'Cuotas solo tarjetas emitidas en bancos locales de Uruguay.',
	},

	// Copa (CM)
	{
		carrierCode: 'CM',
		carrierName: 'Copa Airlines',
		cardLabel: 'Mastercard / Oca',
		cardStamps: ['mastercard', 'oca'],
		merchantNumber: '11078998',
		installmentsLabel: '1 a 6',
		validUntil: '31/12/2026',
	},
	{
		carrierCode: 'CM',
		carrierName: 'Copa Airlines',
		cardLabel: 'Visa',
		cardStamps: ['visa'],
		merchantNumber: '11078998',
		installmentsLabel: '1 a 6',
		validUntil: '31/12/2026',
	},
	{
		carrierCode: 'CM',
		carrierName: 'Copa Airlines',
		cardLabel: 'American Express',
		cardStamps: ['amex'],
		merchantNumber: '9960068260',
		installmentsLabel: '1 a 6',
		validUntil: '31/12/2026',
	},

	// Delta (DL)
	{
		carrierCode: 'DL',
		carrierName: 'Delta',
		cardLabel: 'American Express',
		cardStamps: ['amex'],
		merchantNumber: '9960120087',
		installmentsLabel: '1 a 6',
		validUntil: '31/12/2026',
	},
	{
		carrierCode: 'DL',
		carrierName: 'Delta',
		cardLabel: 'Master',
		cardStamps: ['mastercard'],
		merchantNumber: '15975410',
		installmentsLabel: '1 a 6',
		validUntil: '31/12/2026',
	},
	{
		carrierCode: 'DL',
		carrierName: 'Delta',
		cardLabel: 'Visa',
		cardStamps: ['visa'],
		merchantNumber: '2006810001',
		installmentsLabel: '1 a 6',
		validUntil: '31/12/2026',
	},

	// Gol (G3)
	{
		carrierCode: 'G3',
		carrierName: 'Gol',
		cardLabel: 'Master',
		cardStamps: ['mastercard'],
		merchantNumber: '17373656',
		installmentsLabel: '1 a 6',
		validUntil: '31/07/2026',
	},
	{
		carrierCode: 'G3',
		carrierName: 'Gol',
		cardLabel: 'Visa',
		cardStamps: ['visa'],
		merchantNumber: '2026394980',
		installmentsLabel: '1 a 6',
		validUntil: '31/07/2026',
	},
	{
		carrierCode: 'G3',
		carrierName: 'Gol',
		cardLabel: 'American Express',
		cardStamps: ['amex'],
		merchantNumber: '9960074615',
		installmentsLabel: '1 a 6',
		validUntil: '31/07/2026',
	},
	{
		carrierCode: 'G3',
		carrierName: 'Gol',
		cardLabel: 'Visa',
		cardStamps: ['visa'],
		merchantNumber: '2026394980',
		installmentsLabel: '10',
		validUntil: '31/07/2026',
	},
	{
		carrierCode: 'G3',
		carrierName: 'Gol',
		cardLabel: 'Master',
		cardStamps: ['mastercard'],
		merchantNumber: '17373656',
		installmentsLabel: '10',
		validUntil: '31/07/2026',
	},

	// Iberia (IB)
	{
		carrierCode: 'IB',
		carrierName: 'Iberia',
		cardLabel: 'American Express',
		cardStamps: ['amex'],
		merchantNumber: '9960119139',
		installmentsLabel: '1',
		validUntil: '31/12/2026',
	},
	{
		carrierCode: 'IB',
		carrierName: 'Iberia',
		cardLabel: 'Master',
		cardStamps: ['mastercard'],
		merchantNumber: '15970132',
		installmentsLabel: '1 a 12',
		validUntil: '31/12/2026',
	},
	{
		carrierCode: 'IB',
		carrierName: 'Iberia',
		cardLabel: 'Visa',
		cardStamps: ['visa'],
		merchantNumber: '2043603003',
		installmentsLabel: '10',
		validUntil: '31/07/2026',
	},
	{
		carrierCode: 'IB',
		carrierName: 'Iberia',
		cardLabel: 'Visa',
		cardStamps: ['visa'],
		merchantNumber: '2043603003',
		installmentsLabel: '1 a 6',
		validUntil: '31/07/2026',
	},

	// LATAM (LA)
	{
		carrierCode: 'LA',
		carrierName: 'LATAM',
		cardLabel: 'American Express',
		cardStamps: ['amex'],
		merchantNumber: '9960028462',
		installmentsLabel: '1 a 6',
		validUntil: '31/12/2026',
	},
	{
		carrierCode: 'LA',
		carrierName: 'LATAM',
		cardLabel: 'Master',
		cardStamps: ['mastercard'],
		merchantNumber: '02482226',
		installmentsLabel: '1 a 6',
		validUntil: '31/12/2026',
	},
	{
		carrierCode: 'LA',
		carrierName: 'LATAM',
		cardLabel: 'Visa',
		cardStamps: ['visa'],
		merchantNumber: '02482226',
		installmentsLabel: '1 a 6',
		validUntil: '31/12/2026',
	},
	{
		carrierCode: 'LA',
		carrierName: 'LATAM',
		cardLabel: 'Visa',
		cardStamps: ['visa'],
		merchantNumber: '02482226',
		installmentsLabel: 'Hasta 12',
		validUntil: '31/12/2026',
	},
	{
		carrierCode: 'LA',
		carrierName: 'LATAM',
		cardLabel: 'Master',
		cardStamps: ['mastercard'],
		merchantNumber: '02482226',
		installmentsLabel: 'Hasta 12',
		validUntil: '31/12/2026',
	},

	// Lufthansa (LH)
	{
		carrierCode: 'LH',
		carrierName: 'Lufthansa',
		cardLabel: '—',
		cardStamps: ['visa', 'mastercard', 'amex', 'oca', 'other'],
		merchantNumber: 'NOT_ACCEPTED',
		installmentsLabel: '—',
		validUntil: '31/12/2026',
		notes: 'Por el momento no acepta tarjetas.',
	},

	// United (UA)
	{
		carrierCode: 'UA',
		carrierName: 'United',
		cardLabel: 'American Express',
		cardStamps: ['amex'],
		merchantNumber: 'GDS_ONLY',
		installmentsLabel: '1',
		validUntil: '31/12/2026',
		notes: 'Solo por GDS. Un pago. Venta internacional.',
	},
	{
		carrierCode: 'UA',
		carrierName: 'United',
		cardLabel: 'Visa',
		cardStamps: ['visa'],
		merchantNumber: 'GDS_ONLY',
		installmentsLabel: '1',
		validUntil: '31/12/2026',
		notes: 'Solo por GDS. Un pago. Venta internacional.',
	},
	{
		carrierCode: 'UA',
		carrierName: 'United',
		cardLabel: 'Master',
		cardStamps: ['mastercard'],
		merchantNumber: 'GDS_ONLY',
		installmentsLabel: '1',
		validUntil: '31/12/2026',
		notes: 'Solo por GDS. Un pago. Venta internacional.',
	},

	// Paranair (ZP)
	{
		carrierCode: 'ZP',
		carrierName: 'Paranair',
		cardLabel: 'Visa',
		cardStamps: ['visa'],
		merchantNumber: '2098754001',
		installmentsLabel: '1',
		validUntil: '31/12/2026',
	},
	{
		carrierCode: 'ZP',
		carrierName: 'Paranair',
		cardLabel: 'Mastercard',
		cardStamps: ['mastercard'],
		merchantNumber: '25258601',
		installmentsLabel: '1',
		validUntil: '31/12/2026',
	},

	// Sky Airlines (H2)
	{
		carrierCode: 'H2',
		carrierName: 'Sky Airlines',
		cardLabel: 'Visa',
		cardStamps: ['visa'],
		merchantNumber: '30739784',
		installmentsLabel: 'Hasta 6',
		validUntil: '31/12/2026',
	},
	{
		carrierCode: 'H2',
		carrierName: 'Sky Airlines',
		cardLabel: 'Master',
		cardStamps: ['mastercard'],
		merchantNumber: '30739784',
		installmentsLabel: 'Hasta 6',
		validUntil: '31/12/2026',
	},
	{
		carrierCode: 'H2',
		carrierName: 'Sky Airlines',
		cardLabel: 'American Express',
		cardStamps: ['amex'],
		merchantNumber: '9960404960',
		installmentsLabel: 'Hasta 6',
		validUntil: '31/12/2026',
	},
];
